import React, { useRef, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Typography, useMediaQuery, Icon, Avatar } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useQuery, useQueryClient } from 'react-query'

// Local Imports
import AdxTabsPanel from 'components/reusable/tab/AdxTabsPanel'
import AdxTabsScrollable from 'components/reusable/tab/AdxTabsScrollable'
import WorkDaysConfiguration from 'module/leave/pages/editOperationDays/components/WorkDaysConfiguration'
import HolidaysConfiguration from 'module/leave/pages/editOperationDays/components/HolidaysConfiguration'
import DefaultOrganizationLogo from 'assets/images/default_company_photo.png'
import AdxEmployeeAvatar, {
  setMaxInSingleLine,
} from 'components/reusable/employeeAvatar/AdxEmployeeAvatar'

// API service
import { onboardingJobInformation } from 'services/api/authentication'
import AdxButton from 'components/custom/AdxButton'

const EditOperationDay = () => {
  let navigate = useNavigate()
  let theme = useTheme()
  let queryClient = useQueryClient()
  const {
    // isLoading,
    // error,
    data: companyData,
  } = useQuery('onboardingJobInfo', async () => {
    const { data } = await onboardingJobInformation()
    return data
  })

  const [buttonLoading, setButtonLoading] = useState({
    workday: false,
    holiday: false,
  })
  const [canNavigate, setCanNavigate] = useState({
    workday: false,
    holiday: false,
  })
  const [avatarWidth, setAvatarWidth] = useState()

  // Ref declaration
  const refButton = useRef('button')
  const refButton2 = useRef('button')
  const avatarContainer = useRef(null)

  const matches = useMediaQuery(theme.breakpoints.up('lg'))

  const navigateBack = () => {
    navigate(-1)
    queryClient.removeQueries('onboardingJobInfo')
  }

  useEffect(() => {
    if (canNavigate.holiday && canNavigate.workday) {
      navigate(-1)
      queryClient.removeQueries('onboardingJobInfo')
    }
  }, [canNavigate, navigate]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setAvatarWidth(avatarContainer?.current?.clientWidth)
  }, [companyData])

  const Header = () => {
    return (
      <Box
        display={'flex'}
        justifyContent={'space-between'}
        alignItems={'center'}
        sx={{ padding: 3, boxSizing: 'border-box' }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h4" fontWeight="bold">
            Operation Days
          </Typography>
          <Typography variant="h4" color="light-gray-text.main">
            All changes made will affect leave requests
          </Typography>
        </Box>

        <Icon sx={{ fontSize: 30, cursor: 'pointer' }} onClick={navigateBack}>
          close
        </Icon>
      </Box>
    )
  }

  return (
    <Box display={'flex'} flexDirection={'row'}>
      <Box
        className="leftConfiguration"
        sx={{
          overflowY: 'hidden',
          width: { xs: '100vw', lg: '70vw' },
        }}
      >
        <Header />
        <AdxTabsScrollable sxPanel={{ height: 'calc(100vh - 250px)' }}>
          <AdxTabsPanel label="Work Days">
            <Box sx={{ boxSizing: 'border-box', px: 3, py: 2 }}>
              <WorkDaysConfiguration
                ref={refButton}
                setButtonLoading={setButtonLoading}
                setCanNavigate={setCanNavigate}
              />
            </Box>
          </AdxTabsPanel>
          <AdxTabsPanel label="Holidays">
            <Box sx={{ boxSizing: 'border-box', px: 3, py: 2 }}>
              <HolidaysConfiguration
                ref={refButton2}
                setButtonLoading={setButtonLoading}
                setCanNavigate={setCanNavigate}
              />
            </Box>
          </AdxTabsPanel>
        </AdxTabsScrollable>
      </Box>
      {matches ? (
        <Box
          className="Company Details"
          sx={{
            width: '30vw',
            height: '100vh',
            backgroundColor: 'gray-background.main',

            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: 2,
            boxSizing: 'border-box',
          }}
        >
          <Avatar
            alt="company logo"
            src={companyData?.organization_logo_url || DefaultOrganizationLogo}
            sx={{
              width: '150px',
              height: '150px',
              my: 4,
            }}
          />
          <Typography variant="h3" color="dark-text.main">
            {companyData?.organization_name}
          </Typography>
          <Typography variant="subtitle1" color="light-gray-text.main">
            {companyData?.state}, {companyData?.country}
          </Typography>
          <Box
            ref={avatarContainer}
            sx={{
              width: 1,
              my: 1,

              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography variant="subtitle2" color="light-gray-text.main">
              Employees
            </Typography>
            <Box
              sx={{
                mt: 1,
                alignSelf: 'center',
                boxSizing: 'border-box',
              }}
            >
              <AdxEmployeeAvatar
                variant
                data={[
                  ...(companyData?.existing_staff || []),
                  ...new Array(companyData?.staff_remaining_count),
                ]}
                max={
                  companyData
                    ? setMaxInSingleLine(avatarWidth, true, companyData?.existing_staff?.length)
                    : 15
                }
                tooltip
                tooltipMax={companyData?.existing_staff?.length}
                noRemainingTooltip
              />
            </Box>
          </Box>
          <Box sx={{ width: 1, my: 1 }}>
            <Typography variant="subtitle2" color="light-gray-text.main">
              Address
            </Typography>
            <Typography variant="subtitle2" color="dark-text.main">
              {companyData?.formatted_address}
            </Typography>
          </Box>
        </Box>
      ) : null}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'end',
          position: 'fixed',
          bottom: 0,
          width: 1,
          p: 3,
          gap: 1,
          bgcolor: 'primary.light',
        }}
      >
        <AdxButton variant={'outlined'} onClick={navigateBack} sx={{ minWidth: '100px' }}>
          Cancel
        </AdxButton>
        <AdxButton
          variant={'contained'}
          isLoading={buttonLoading.holiday || buttonLoading.workday}
          disabled={buttonLoading.holiday || buttonLoading.workday}
          onClick={() => {
            setButtonLoading({ workday: true, holiday: true })
            refButton.current.handleSubmit()
            refButton2.current.handleSubmit()
          }}
          sx={{ minWidth: '200px' }}
        >
          Save
        </AdxButton>
      </Box>
    </Box>
  )
}

export default EditOperationDay
